import "./CurrentPlanPageStyles.scss";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AccountPageWrapper } from "components/AccountPageWrapper";
import { ComponentItemFallback } from "components/fallback/ComponentItemFallback";
import { LoadingSection } from "components/LoadingSection";
import { PlanInfo } from "components/PlanInfo";
import { SelectInput } from "components/SelectInput";
import { SubmitButton } from "components/SubmitButton";
import { SwitchCheckbox } from "components/SwitchCheckbox/SwitchCheckbox";
import { endOfTomorrow } from "date-fns";
import { getContracts, updateCurrentPlan } from "queries/contract_manager";
import { formatAvailablePlans } from "queries/pricing.utils";
import React, {
  ChangeEvent,
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { setNotificationBanner } from "reducers/notificationBanner";
import { setPlanAsAtTomorrow } from "reducers/planAsAtTomorrow";
import { useAppDispatch, useAppSelector } from "reduxUtils/hook";
import {
  NotificationBannerNames,
  NotificationBannerTypes,
} from "utils/constants";
import { jwtClient } from "utils/jwtClient";
import { getCurrentPlan, getOtherPlans } from "utils/plans/plans";
import { replaceContentParams } from "utils/stringFormat/contentParams";

import content from "./static/content.json";
const {
  changePlanInfo,
  changePlanHeader,
  changePlanHint,
  notificationBody,
  notificationHeader,
  otherPlansHeader,
  pageHeader,
  submitLabel,
} = content;

export const CurrentPlanPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [selectedPlanId, setSelectedPlanId] = useState(undefined);
  const { pathname } = useLocation();
  const [changePlanInfoText, setChangePlanInfoText] = useState(undefined);
  const [isGstInclusive, setIsGstInclusive] = useState<boolean>(false);

  const { customerNumber, supplyNodeRef } = useAppSelector(
    (store) => store.currentAccount,
  );
  const {
    displayName: currentPlanName,
    description: currentPlanDescription,
    id: currentPlanId,
    isCurrentProductError,
  } = useAppSelector((store) => store.currentProduct);

  const { rateCards } = useAppSelector((store) => store.rateCards);

  const planAsAtTomorrow = useAppSelector((store) => store.planAsAtTomorrow);

  const { availablePlans, isAvailablePlansError, isAvailablePlansLoading } =
    useAppSelector((store) => store.availablePlans);

  const formattedRateCards = useMemo(
    () => formatAvailablePlans(rateCards),
    [rateCards],
  );

  const currentPlan: AvailablePlan = useMemo(
    () => getCurrentPlan(availablePlans, currentPlanId),
    [availablePlans, currentPlanId],
  );

  const otherPlans: AvailablePlan[] = useMemo(
    () => getOtherPlans(availablePlans, currentPlanId),
    [currentPlanId],
  );

  const currentAccount = useAppSelector((store) => store.currentAccount);

  // if there's no 'planAsAtTomorrow' in state, fetch the contract as at tomorrow - used to display change plan info box
  const { data: contractAsAtTomorrow } = useQuery(
    ["futurecontractinfo", { supplyNodeRef: currentAccount.supplyNodeRef }],
    () =>
      getContracts({
        customerNumber: currentAccount.customerNumber,
        supplyNodeRef: currentAccount.supplyNodeRef,
        jwtClient,
        asAtDate: endOfTomorrow().toISOString(),
      }),
    {
      enabled: !planAsAtTomorrow?.planId,
    },
  );

  useEffect(() => {
    if (contractAsAtTomorrow?.id && !planAsAtTomorrow?.planId) {
      dispatch(setPlanAsAtTomorrow({ planId: contractAsAtTomorrow.id }));
    }
  }, [contractAsAtTomorrow?.id]);

  const newPlanName = useMemo(() => {
    return availablePlans?.find((ap) => ap.id === planAsAtTomorrow.planId)
      ?.productName;
  }, [planAsAtTomorrow?.planId]);

  // if the current plan and plan as at tomorrow are not equal, show change plan info
  useEffect(() => {
    if (!planAsAtTomorrow?.planId) return;
    if (newPlanName && planAsAtTomorrow.planId !== currentPlanId) {
      const planChangeInfo = replaceContentParams(changePlanInfo || "", {
        planName: newPlanName,
      });

      setChangePlanInfoText(planChangeInfo);
    }
    if (planAsAtTomorrow.planId === currentPlanId) {
      setChangePlanInfoText(undefined);
    }
  }, [planAsAtTomorrow, currentPlanId, newPlanName]);

  const { isLoading, mutate } = useMutation({
    mutationFn: (planId: string) =>
      updateCurrentPlan({
        customerNumber,
        jwtClient,
        planId,
        supplyNodeRef,
      }),
    onSuccess: (data) => {
      const planId = data?.planId;
      dispatch(setPlanAsAtTomorrow({ planId }));
      dispatch(
        setNotificationBanner({
          type: NotificationBannerTypes.success,
          name: NotificationBannerNames.updatePlan,
          page: pathname,
        }),
      );
    },
    onError: () => {
      dispatch(
        setNotificationBanner({
          type: NotificationBannerTypes.error,
          name: NotificationBannerNames.updatePlan,
          page: pathname,
        }),
      );
    },
  });

  const selectOptions = [
    { label: `${currentPlanName} (current)`, value: currentPlanId },
    ...otherPlans.map(({ productName, id }) => ({
      label: productName,
      value: id,
    })),
  ];

  if (isCurrentProductError) {
    return <ComponentItemFallback componentTitle={"Current plan details"} />;
  }

  const handlePlanSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedPlanId(e.target.value);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate(selectedPlanId);
  };

  const handleGstInclusionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setIsGstInclusive(checked);
  };

  return (
    <AccountPageWrapper headerLabel={pageHeader} hasCancelButton={false}>
      <div className="spacer spacer--medium" />
      <div className="switch_section">
        <SwitchCheckbox
          name="gstInclusion"
          id="gstInclusionSwitch1"
          onChange={handleGstInclusionChange}
          checked={isGstInclusive}
          label="GST Inclusion"
          aria-label="switch GST Inclusion"
        />
        <label className="switch_section__label">
          Prices {isGstInclusive ? "including" : "excluding"} GST
        </label>
      </div>
      <div className="current_plan_page">
        {currentPlanId && (
          <PlanInfo
            id={currentPlanId}
            name={currentPlanName}
            description={currentPlanDescription}
            rates={{
              subscriptionRates: currentPlan
                ? currentPlan.subscriptionRates
                : formattedRateCards[0]?.subscriptionRates,
              usageRates: currentPlan
                ? currentPlan.usageRates
                : formattedRateCards[0]?.usageRates,
            }}
            isGstInclusive={isGstInclusive}
          />
        )}
        <hr className="content_divider spacer spacer--medium" />
        <section>
          <h2 className="section-title heading h3">{otherPlansHeader}</h2>
          <div className="switch_section">
            <SwitchCheckbox
              name="gstInclusion"
              id="gstInclusionSwitch2"
              onChange={handleGstInclusionChange}
              checked={isGstInclusive}
              label="GST Inclusion"
              aria-label="switch GST Inclusion"
            />
            <label className="switch_section__label">
              Prices {isGstInclusive ? "including" : "excluding"} GST
            </label>
          </div>
          <div className="notification_banner notification_notice-flick icon spacer">
            <p className="heading heading_paragraph">{notificationHeader}</p>
            <p className="body_paragraph notification_body">
              {notificationBody}
              <span className="spacer spacer--large" />
              Prices {isGstInclusive ? "include" : "exclude"} GST.
            </p>
          </div>
        </section>
        {isAvailablePlansLoading && <LoadingSection />}
        {isAvailablePlansError && (
          <ComponentItemFallback componentTitle={"Other plans"} />
        )}
        {!isAvailablePlansLoading &&
          !isAvailablePlansError &&
          otherPlans.length === 0 && (
            <p>There are currently no other plans available to you.</p>
          )}
        {otherPlans &&
          otherPlans.map(
            ({
              productName,
              description,
              id,
              subscriptionRates,
              usageRates,
            }) => {
              return (
                <PlanInfo
                  key={id}
                  name={productName}
                  description={description}
                  id={id}
                  rates={{
                    subscriptionRates,
                    usageRates: usageRates,
                  }}
                  isGstInclusive={isGstInclusive}
                />
              );
            },
          )}
        <hr className="content_divider spacer spacer--medium" />
        <div>
          <h2 className="section-title heading h3">{changePlanHeader}</h2>
          {changePlanInfoText && (
            <div className="notification_banner notification_notice icon spacer">
              <p className="heading heading_paragraph">{changePlanInfoText}</p>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <SelectInput
              ariaLabel="Change plan"
              name="change-plan"
              hint={changePlanHint}
              defaultOption={selectOptions[0].value}
              options={selectOptions}
              handleOnChange={handlePlanSelect}
              value={selectedPlanId}
            />
            <span className="spacer spacer--small"></span>
            <SubmitButton
              isSubmitDisabled={
                isLoading ||
                !selectedPlanId ||
                selectedPlanId === currentPlanName
              }
              label={submitLabel}
            />
          </form>
        </div>
      </div>
    </AccountPageWrapper>
  );
};
